/**
 * Human-readable run duration formatting (#114).
 *
 * Pure, side-effect-free helpers so they're trivially unit-testable. Used by the
 * Runs list to render how long a run took (Started → Ended), and — for a run that
 * hasn't ended — how long it has been running so far (a plain `now − start` at
 * render, no live ticker).
 */

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

/**
 * Format an elapsed duration (in milliseconds) as a compact human string:
 *   - sub-minute:  `0.4s`, `3.2s`, `59s`   (one decimal under 10s, whole seconds otherwise)
 *   - minutes:     `1m 42s`, `9m 0s`
 *   - hours:       `1h 4m`, `2h 0m`        (seconds dropped once we're in hours)
 *
 * Returns `null` for null/negative/non-finite input so callers can fall back to a
 * dash. Exact zero renders `0.0s`; any negative is treated as "no meaningful
 * duration" → null.
 */
export function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;

  if (ms < MS_PER_MINUTE) {
    const seconds = ms / MS_PER_SECOND;
    // One decimal below 10s (e.g. 3.2s); whole seconds from 10s up (e.g. 42s).
    return seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`;
  }

  if (ms < MS_PER_HOUR) {
    const minutes = Math.floor(ms / MS_PER_MINUTE);
    const seconds = Math.round((ms % MS_PER_MINUTE) / MS_PER_SECOND);
    // Rounding can push seconds to 60 — carry into the minute.
    if (seconds === 60) return `${minutes + 1}m 0s`;
    return `${minutes}m ${seconds}s`;
  }

  const hours = Math.floor(ms / MS_PER_HOUR);
  const minutes = Math.round((ms % MS_PER_HOUR) / MS_PER_MINUTE);
  if (minutes === 60) return `${hours + 1}h 0m`;
  return `${hours}h ${minutes}m`;
}

/**
 * Compute a run's duration (in ms) from ISO timestamps: `end − start`. Returns null
 * if either bound is missing or the range is invalid — the caller decides how to show
 * an in-progress or unknown duration.
 */
export function durationMs(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): number | null {
  if (!startIso || !endIso) return null;
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  const delta = end - start;
  return delta >= 0 ? delta : null;
}
